/* Lems | B2B tax exemption certificate collection
 *
 * Talks to a Make webhook, never to Avalara directly -- the AvaTax account id and
 * license key must never reach the browser.
 *
 * Flow:
 *   1. POST {action:"start"}  -> Make ensures an ECM customer record exists and
 *      returns a CertExpress requestLink (or tells us a certificate already exists).
 *   2. We open that link in a new tab. The customer fills in and signs the
 *      certificate on Avalara's own portal.
 *   3. We poll {action:"status"} until Avalara reports a valid certificate for
 *      that customer + country + region.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-lems-exempt] .lems-exempt__card[data-endpoint]');
  if (!root) return;

  var form = root.querySelector('[data-exempt-form]');
  var statusEl = root.querySelector('[data-status]');
  var submitBtn = root.querySelector('[data-submit]');
  var submitLabel = root.querySelector('[data-submit-label]');
  var spinner = root.querySelector('[data-spinner]');

  var endpoint = root.dataset.endpoint;
  var customerId = root.dataset.customerId;
  // Empty unless the shopper is trading as a B2B company. Make uses it to pick
  // the ECM customerCode: company id when present, customer id otherwise.
  var companyId = root.dataset.companyId || '';
  var defaultCountry = root.dataset.defaultCountry || 'US';
  var pollMs = (parseInt(root.dataset.pollSeconds, 10) || 10) * 1000;

  var POLL_LIMIT = 60; // ~10 min at the default 10s interval
  var pollTimer = null;
  var pollCount = 0;
  var certWindow = null;

  if (!endpoint) {
    show('error', 'This form is not configured yet. Please contact us and mention "missing exemption endpoint".');
    if (submitBtn) submitBtn.disabled = true;
    return;
  }

  function show(kind, html) {
    statusEl.hidden = false;
    statusEl.className = 'lems-exempt__status lems-exempt__status--' + kind;
    statusEl.innerHTML = html;
  }

  function busy(isBusy, label) {
    submitBtn.disabled = isBusy;
    spinner.hidden = !isBusy;
    if (label) submitLabel.textContent = label;
  }

  function call(payload) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.text().then(function (text) {
        var data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch (e) {
          throw new Error('Unexpected response from the exemption service.');
        }
        if (!res.ok || data.ok === false) {
          throw new Error(data.message || 'The exemption service returned an error (' + res.status + ').');
        }
        return data;
      });
    });
  }

  function selectedRegion() {
    var opt = form.exposureZoneId.options[form.exposureZoneId.selectedIndex];
    return (opt && opt.dataset.region) || root.dataset.defaultRegion || '';
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!form.reportValidity()) return;

    var region = selectedRegion();
    if (!region) {
      show('error', 'Please choose the state you are claiming exemption in.');
      return;
    }

    // Open the tab synchronously inside the click so popup blockers allow it;
    // we navigate it once Make hands back the CertExpress link.
    certWindow = window.open('', '_blank');

    busy(true, 'Preparing your form…');
    show('info', 'Setting up your certificate request with Avalara…');

    call({
      action: 'start',
      shopifyCustomerId: customerId,
      shopifyCompanyId: companyId,
      exposureZoneId: form.exposureZoneId.value,
      exemptReasonId: form.exemptReasonId.value,
      region: region,
      country: defaultCountry
    })
      .then(function (data) {
        if (data.status === 'already_exempt') {
          if (certWindow) certWindow.close();
          busy(false, 'Submitted');
          show('success',
            '<strong>' + esc(data.accountName || 'Your account') +
            ' already has a valid certificate on file for ' + esc(region) + '.</strong>' +
            '<p>No further action needed — tax is removed from qualifying orders automatically.</p>');
          return;
        }

        if (!data.requestLink) {
          throw new Error('Avalara did not return a certificate link. Please try again or contact us.');
        }

        if (certWindow) {
          certWindow.location.href = data.requestLink;
        } else {
          window.location.href = data.requestLink;
          return;
        }

        busy(false, 'Waiting for your certificate…');
        show('info',
          '<strong>Your certificate form is open in a new tab.</strong>' +
          '<p>Complete and sign it there. This page updates automatically when Avalara receives it — ' +
          'you can also <a href="' + esc(data.requestLink) + '" target="_blank" rel="noopener">reopen the form</a>.</p>');

        startPolling(region);
      })
      .catch(function (err) {
        if (certWindow) certWindow.close();
        busy(false, root.querySelector('[data-submit-label]').dataset.original || 'Continue to certificate form');
        show('error', esc(err.message));
      });
  });

  function startPolling(region) {
    stopPolling();
    pollCount = 0;
    pollTimer = setInterval(function () {
      pollCount++;
      if (pollCount > POLL_LIMIT) {
        stopPolling();
        show('info',
          '<strong>Still waiting on Avalara.</strong>' +
          '<p>If you finished the form, it can take a few minutes to appear. ' +
          'Refresh this page later to check, or contact us and we’ll confirm.</p>');
        return;
      }

      call({
        action: 'status',
        shopifyCustomerId: customerId,
        shopifyCompanyId: companyId,
        region: region,
        country: defaultCountry
      })
        .then(function (data) {
          if (data.status === 'exempt') {
            stopPolling();
            show('success',
              '<strong>Certificate received — you’re all set.</strong>' +
              '<p>Your exemption for ' + esc(region) + ' is on file. Sales tax will be removed from ' +
              'qualifying orders shipped to that state.</p>');
          }
        })
        .catch(function () {
          /* transient -- keep polling until the limit */
        });
    }, pollMs);

    // Re-check as soon as they come back to this tab.
    document.addEventListener('visibilitychange', onVisible);
  }

  function onVisible() {
    if (document.visibilityState === 'visible' && pollTimer) pollCount = Math.max(0, pollCount - 1);
  }

  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
    document.removeEventListener('visibilitychange', onVisible);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (submitLabel) submitLabel.dataset.original = submitLabel.textContent;
})();
